import React, { useEffect, useMemo, useState, useCallback } from "react";
import { CheckPicker, Checkbox } from "rsuite";
import styled from "styled-components";

export const FilterSystems = ({
  report,
  currentReportSys,
  setCurrentReportSys,
  allSystems = [],
}) => {
  // ────────────────────────────────────────────────────────────────
  // 1) UI state
  // Default: show customer ID if present; toggle shows Avante (system.id)
  const [showAvanteId, setShowAvanteId] = useState(false);

  const [selectedCustomerNames, setSelectedCustomerNames] = useState([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [selectedModalities, setSelectedModalities] = useState([]);

  // Ensure we always treat value as an array
  const selectedIds = Array.isArray(currentReportSys) ? currentReportSys : [];

  // Initialize from report once
  useEffect(() => {
    if (Array.isArray(report?.systems_list)) {
      setCurrentReportSys(report.systems_list);
    } else if (report?.systems_list == null) {
      setCurrentReportSys([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // ────────────────────────────────────────────────────────────────
  // 2) Option builders
  const customerNames = useMemo(() => {
    const set = new Set(allSystems.map(s => s.customer_name).filter(Boolean));
    return [...set].sort().map(name => ({ label: name, value: name }));
  }, [allSystems]);

  const manufacturers = useMemo(() => {
    const set = new Set(allSystems.map(s => s.manufacturer).filter(Boolean));
    return [...set].sort().map(m => ({ label: m, value: m }));
  }, [allSystems]);

  const modalities = useMemo(() => {
    const set = new Set(allSystems.map(s => s.modality).filter(Boolean));
    return [...set].sort().map(m => ({ label: m, value: m }));
  }, [allSystems]);


  // ────────────────────────────────────────────────────────────────
  // 3) Filtering & visible systems
  const filteredData = useMemo(() => {
    return allSystems.filter(item =>
      (selectedCustomerNames.length === 0 || selectedCustomerNames.includes(item.customer_name)) &&
      (selectedManufacturers.length === 0 || selectedManufacturers.includes(item.manufacturer)) &&
      (selectedModalities.length === 0 || selectedModalities.includes(item.modality))
    );
  }, [allSystems, selectedCustomerNames, selectedManufacturers, selectedModalities]);

  // Label builder (single source of truth)
  const makeLabel = useCallback((sys) => {
    const idPart = showAvanteId
      ? sys.id // Avante ID
      : (sys.cus_sys_id || sys.id); // default: customer ID if present, else Avante
    return `${idPart} - ${sys.manufacturer} - ${sys.modality}`;
  }, [showAvanteId]);

  // Map filtered systems to CheckPicker {label, value} items
  const filteredSystems = useMemo(() => {
    // Deduplicate by system.id in case allSystems includes dupes
    const seen = new Set();
    const rows = [];
    for (const sys of filteredData) {
      if (sys?.id == null || seen.has(sys.id)) continue;
      seen.add(sys.id);
      rows.push({ value: sys.id, label: makeLabel(sys) });
    }
    return rows;
  }, [filteredData, makeLabel]);

  const filteredIds = useMemo(() => filteredSystems.map(s => s.value), [filteredSystems]);


  // ────────────────────────────────────────────────────────────────
  // 4) Handlers
  const handleChangeSystem = useCallback((ids) => {
    setCurrentReportSys(Array.isArray(ids) ? ids : []);
  }, [setCurrentReportSys]);

  // rsuite Checkbox onChange signature: (value, checked, event)
  const handleCheckAll = useCallback((_, checked) => {
    setCurrentReportSys(checked ? filteredIds : []);
  }, [filteredIds, setCurrentReportSys]);

  const handleToggleAvanteId = useCallback((_, checked) => {
    setShowAvanteId(checked);
  }, []);


  // Indeterminate/checked state only compares against the currently filtered list
  const selectedInFilteredCount = useMemo(() => {
    if (!filteredIds.length || !selectedIds.length) return 0;
    const selectedSet = new Set(selectedIds);
    return filteredIds.reduce((acc, id) => acc + (selectedSet.has(id) ? 1 : 0), 1*0);
  }, [filteredIds, selectedIds]);

  const allChecked = filteredIds.length > 0 && selectedInFilteredCount === filteredIds.length;
  const indeterminate = selectedInFilteredCount > 0 && !allChecked;


  // ────────────────────────────────────────────────────────────────
  // 5) Render
  return (
    <FilterContainer>
      <FlexRow>
        <CheckPicker data={customerNames} onChange={setSelectedCustomerNames} placeholder="Customer" />
        <CheckPicker data={manufacturers} onChange={setSelectedManufacturers} placeholder="Manufacturer" />
        <CheckPicker data={modalities} onChange={setSelectedModalities} placeholder="Modality" />
      </FlexRow>

      <FilterContainer>
        <CheckPicker
          data={filteredSystems}
          value={selectedIds}
          onChange={handleChangeSystem}
          placeholder="Systems"
          block
          renderExtraFooter={() => (
            <FooterRow>
              {/* Only render the Check all group when it makes sense */}
              {filteredIds.length > 1 && (
                <div style={footerStyles}>
                  <Checkbox
                    indeterminate={indeterminate}
                    checked={allChecked}
                    onChange={handleCheckAll}
                  >
                    Check all
                  </Checkbox>
                </div>
              )}
              <div style={footerStyles}>
                <Checkbox checked={showAvanteId} onChange={handleToggleAvanteId}>
                  Avante ID
                </Checkbox>
              </div>
            </FooterRow>
          )}
        />
      </FilterContainer>
    </FilterContainer>
  );
};


// ──────────────────────────────────────────────────────────────────
// Styles
const footerStyles = {
  padding: "10px 2px",
  borderTop: "1px solid #e5e5e5",
};

const FlexRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
`;

const FooterRow = styled.div`
  display: flex;
  gap: 1rem;
`;

const FilterContainer = styled.div`
  margin-bottom: 1rem;
`;